/**
 * Sequence Step Evaluation Error Triage Page
 *
 * Admin-tier surface (admin / superadmin / manager — matches the
 * resolve RPC's role gate). Lists unresolved errors grouped by
 * (enrollment_id, step_id) so a single "Resolve" button on each
 * group does exactly what the operator expects: clear the visible
 * errors and kick the enrollment back into evaluation via
 * resolve_sequence_step_evaluation_errors(enrollment_id, step_id).
 *
 * Per audit-AI: the resolve action is intentionally step-scoped (not
 * enrollment-scoped) so it can't silently clear hidden errors on
 * other steps of the same enrollment.
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
  listSequenceStepEvaluationErrors,
  groupErrors,
  resolveSequenceStepErrorGroup,
  getDistinctErrorCodes,
  getSequencesWithErrors,
  type SequenceErrorFilters,
} from "../services/sequenceErrors.service";
import Button from "../components/common/Button";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useToast } from "../components/ui/Toast";
import { formatDate } from "../utils/formatters";

const PAGE_SIZE = 50;

function relativeAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatContactName(
  c?: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null,
): string {
  if (!c) {
    return "(unknown contact)";
  }
  const full = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim();
  return full || c.email || "(no name)";
}

export default function SequenceErrorTriage() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<SequenceErrorFilters>({
    status: "unresolved",
    sequence_id: null,
    error_code: null,
  });
  const [page, setPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const {
    data: result,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sequence-step-errors", filters, page],
    queryFn: () =>
      listSequenceStepEvaluationErrors(filters, { page, limit: PAGE_SIZE }),
  });

  const { data: errorCodes = [] } = useQuery({
    queryKey: ["sequence-step-error-codes", filters.status],
    queryFn: () => getDistinctErrorCodes({ status: filters.status }),
  });

  const { data: sequenceOptions = [] } = useQuery({
    queryKey: ["sequence-step-error-sequences", filters.status],
    queryFn: () => getSequencesWithErrors({ status: filters.status }),
  });

  const groups = useMemo(() => groupErrors(result?.data ?? []), [result?.data]);

  const resolveMutation = useMutation({
    mutationFn: ({
      enrollmentId,
      stepId,
    }: {
      enrollmentId: string;
      stepId: string;
    }) => resolveSequenceStepErrorGroup(enrollmentId, stepId),
    onSuccess: (count) => {
      showToast(
        `Resolved ${count} error${count === 1 ? "" : "s"}. Enrollment will re-evaluate on the next sequence-cron tick.`,
        "success",
      );
      queryClient.invalidateQueries({
        queryKey: ["sequence-step-errors"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sequence-step-error-codes"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sequence-step-error-sequences"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sequence-step-error-count"],
      });
    },
    onError: (err) => {
      showToast(`Resolve failed: ${(err as Error).message}`, "error");
    },
  });

  const totalPages = Math.max(1, Math.ceil((result?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Sequence Step Evaluation Errors
          </h1>
          <p className="mt-2 text-gray-600">
            Triage stuck sequence enrollments where step criteria failed to
            evaluate. Resolving an error kicks the enrollment back into the
            evaluation queue.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className="h-5 w-5" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Status
            </label>
            <select
              value={filters.status ?? "unresolved"}
              onChange={(e) => {
                setFilters((f) => ({
                  ...f,
                  status: e.target.value as SequenceErrorFilters["status"],
                }));
                setPage(1);
              }}
              className="w-full rounded border-gray-300 text-sm"
            >
              <option value="unresolved">Unresolved</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Sequence
            </label>
            <select
              value={filters.sequence_id ?? ""}
              onChange={(e) => {
                setFilters((f) => ({
                  ...f,
                  sequence_id: e.target.value || null,
                }));
                setPage(1);
              }}
              className="w-full rounded border-gray-300 text-sm"
            >
              <option value="">All sequences</option>
              {sequenceOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Error code
            </label>
            <select
              value={filters.error_code ?? ""}
              onChange={(e) => {
                setFilters((f) => ({
                  ...f,
                  error_code: e.target.value || null,
                }));
                setPage(1);
              }}
              className="w-full rounded border-gray-300 text-sm"
            >
              <option value="">All codes</option>
              {errorCodes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end text-xs text-gray-500">
            {result
              ? `${result.total} total error${result.total === 1 ? "" : "s"}`
              : ""}
          </div>
        </div>
      </Card>

      {/* Body */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : groups.length === 0 ? (
        <Card padding="lg">
          <div className="flex flex-col items-center text-center py-8">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mb-3" />
            <h3 className="text-lg font-medium text-gray-900">
              No sequence evaluation errors
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Errors are logged automatically when a sequence step&apos;s
              segment criteria fail to evaluate (missing list reference, cycle,
              depth limit, etc.).
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const key = `${group.enrollment_id}:${group.step_id}`;
            const isExpanded = expandedGroups.has(key);
            const sequence = group.enrollment?.sequence;
            return (
              <Card key={key} padding="md">
                <div className="flex items-start justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      const next = new Set(expandedGroups);
                      if (isExpanded) {
                        next.delete(key);
                      } else {
                        next.add(key);
                      }
                      setExpandedGroups(next);
                    }}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                      <span className="text-base font-medium text-gray-900">
                        {formatContactName(group.contact)}
                      </span>
                      {group.contact?.id && (
                        <Link
                          to={`/contacts/${group.contact.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          view contact ›
                        </Link>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {sequence ? (
                        <Link
                          to={`/sequences/${sequence.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:underline"
                        >
                          {sequence.name}
                        </Link>
                      ) : (
                        "(unknown sequence)"
                      )}
                      {" · "}
                      <span>
                        step:{" "}
                        {group.step?.name ?? group.step_id.substring(0, 8)}
                      </span>
                      {" · "}
                      <span>
                        {group.total_error_count} error
                        {group.total_error_count === 1 ? "" : "s"}
                      </span>
                      {" · "}
                      <span>{relativeAge(group.newest_occurred_at)}</span>
                    </div>
                  </button>
                  <Button
                    variant="primary"
                    onClick={() =>
                      resolveMutation.mutate({
                        enrollmentId: group.enrollment_id,
                        stepId: group.step_id,
                      })
                    }
                    disabled={
                      resolveMutation.isPending ||
                      group.errors.every((e) => e.resolved_at !== null)
                    }
                    className="flex-shrink-0"
                  >
                    Resolve {group.total_error_count}
                  </Button>
                </div>

                {isExpanded && (
                  <div className="mt-4 border-t pt-3 space-y-2">
                    {group.errors.map((err) => (
                      <div
                        key={err.id}
                        className="text-sm bg-gray-50 rounded p-3"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs bg-gray-200 px-2 py-0.5 rounded">
                            {err.error_code}
                          </span>
                          {err.condition_index !== null && (
                            <span className="text-xs text-gray-500">
                              condition #{err.condition_index}
                            </span>
                          )}
                          {err.list_id && (
                            <span className="text-xs text-gray-500">
                              list: {err.list_id.substring(0, 8)}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 ml-auto">
                            {formatDate(err.occurred_at)} ·{" "}
                            {relativeAge(err.occurred_at)}
                          </span>
                          {err.resolved_at && (
                            <span className="text-xs text-green-600">
                              resolved {relativeAge(err.resolved_at)}
                            </span>
                          )}
                        </div>
                        {err.error_message && (
                          <pre className="mt-2 text-xs text-gray-700 whitespace-pre-wrap break-words">
                            {err.error_message}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
